/**
 * T17 — the review/approve/download UI for an artifact's generated media.
 *
 * Renders nothing at all when the artifact carries no `payload.media` (the
 * vast majority of specialists never generate media — no-silent-absence
 * cuts both ways: a specialist that never tries shows nothing, rather than
 * an empty "no images" card everywhere). When media WAS attempted,
 * `media.status` always explains the outcome — generated assets render
 * inline (image/video switch), any other status renders a Banner naming
 * why media is absent.
 *
 * T18/D3 — async video landed here with NO changes to the fetch/render
 * path itself: `video_pending` (decision 11A — video is never inline)
 * renders the same skipped_*-shaped Banner branch every other non-
 * terminal status already used, just with its own copy and an 'info'
 * tone (a render in progress is not a warning). Once
 * jobs/media_video_worker.py flips the artifact to the SAME `generated`
 * status images use, the existing fetch effect below (keyed off
 * `media.status !== 'generated'`) picks it up with no video-specific
 * code path — the asset/kind switch a few dozen lines down already
 * renders `<video>` for `asset.kind === 'video'`.
 *
 * PRESIGN EXPIRY (quality review, Important 7): presigned URLs are
 * short-lived (media_storage_service.PRESIGNED_URL_EXPIRY_SECONDS,
 * plumbed through as each asset's `expires_in`). A specialist detail page
 * has no reason to reload on its own, so a page left open past that
 * window would otherwise show silently-broken images forever. Two
 * independent refreshes cover it: a PROACTIVE timer scheduled shortly
 * before the soonest asset in the current batch expires, and a REACTIVE,
 * ref-guarded one-time refetch if an asset's <img>/<video> fails to load
 * anyway (covers a backgrounded tab whose timer never fired).
 *
 * Both refreshes use ``background: true`` (a separate ``refreshing``
 * flag, never ``loading``) once assets already exist — ``loading`` would
 * unmount the assets block (SkeletonBodyText in its place), and
 * remounting a FRESH <img> with the same still-broken URL after a
 * fetch-succeeds/image-still-fails condition (an ad blocker or CSP
 * blocking the R2 host, or the underlying object deleted out from under
 * an otherwise-presignable row) made the reactive refetch loop
 * indefinitely — one API+presign round trip per cycle, forever. The
 * guard re-arms on the image's OWN successful load (``onLoad``/
 * ``onLoadedData``), not on the metadata fetch merely succeeding: a
 * fetch that returns a valid presigned URL for an image that still won't
 * load is exactly the condition above, and only an actual load proves
 * the URL is reachable. Net effect: at most one reactive refetch attempt
 * per failure, never an infinite loop.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris';

import {
  approveArtifact,
  getArtifactAssets,
} from '../../services/marketingSuiteApi';
import type {
  Artifact,
  ArtifactAsset,
  ArtifactMediaInfo,
} from '../../types/marketingSuite';

// Real causes an operator/tenant can act on, not a guess ("the presign
// may have expired" was never actually true for the empty-list case —
// that only happens when the server-side asset lookup itself came back
// empty, e.g. the referenced row is gone or every per-asset presign
// failed; the reactive/proactive refresh above is what actually handles
// real presign expiry, on an already-populated list).
//
// I7 (quality re-review, 2026-08-13): six of these statuses are shared
// by BOTH the image (inline) and video (async) media steps —
// skipped_budget_exhausted/quota_exhausted/not_configured/
// provider_error/unexpected_error/storage_error can each be reached by
// either path (video_generation_service.py / media_video_worker.py
// raise/write the exact same MEDIA_ASSETS_STATUS_* constants
// run_service.py's image step does) — but every one of their messages
// below hardcoded "Image"/"Images" nouns, so a VIDEO failure on any of
// these six read as an image failure, and the remedy for
// skipped_quota_exhausted named "images" specifically even when the
// plan's VIDEO quota was what was actually exhausted. The other four
// statuses are genuinely asset-type-specific already and stay as
// plain strings: skipped_deadline (TODOS#16(a)'s whole-batch deadline
// is an image-inline-loop concept with no video equivalent — video's
// request-path work is one fast submission call, module docstring
// video_generation_service.py "PROVIDER ABSTRACTION"), skipped_refused
// (video has no distinct refusal status at all — module docstring "TYPED
// ERRORS", a video safety rejection surfaces as skipped_provider_error
// instead), video_pending and skipped_timeout (video-only outcomes,
// already correctly video-worded).
const STATUS_MESSAGES_ASSET_TYPE_AGNOSTIC: Record<string, string> = {
  // T18/D3: NOT a skipped_* status — the render is in flight
  // (decision 11A, async video). Deliberately worded to invite a
  // return visit rather than read as a failure the way every skipped_*
  // message below does.
  video_pending:
    'Your video is rendering — check back in a few minutes.',
  skipped_deadline:
    'Image generation did not finish in time for this run and was skipped.',
  skipped_timeout:
    'Video generation did not finish in time and was abandoned.',
  skipped_refused:
    'Image generation was refused by the safety system for this prompt.',
};

// {image, video} pairs for the six statuses both media steps can reach.
const STATUS_MESSAGES_BY_ASSET_TYPE: Record<
  string,
  { image: string; video: string }
> = {
  skipped_budget_exhausted: {
    image:
      "Images were not generated — this workspace's monthly AI media budget is used up. It resets next month, or upgrade your plan for a larger budget.",
    video:
      "Your video was not generated — this workspace's monthly AI media budget is used up. It resets next month, or upgrade your plan for a larger budget.",
  },
  skipped_quota_exhausted: {
    image:
      "Images were not generated — this plan's monthly image quota is used up. It resets next month, or upgrade your plan for more images.",
    video:
      "Your video was not generated — this plan's monthly video quota is used up. It resets next month, or upgrade your plan for more videos.",
  },
  skipped_not_configured: {
    image: 'Image generation is not available for this workspace yet.',
    video: 'Video generation is not available for this workspace yet.',
  },
  skipped_storage_error: {
    image:
      'An image was generated but could not be stored — it was not saved.',
    video:
      'A video was generated but could not be stored — it was not saved.',
  },
  skipped_provider_error: {
    image: 'Image generation failed due to a provider error.',
    video: 'Video generation failed due to a provider error.',
  },
  skipped_unexpected_error: {
    image: 'Image generation failed unexpectedly.',
    video: 'Video generation failed unexpectedly.',
  },
};

// T18/D3's own asset-type vocabulary (marketing_suite/catalog.py's
// MEDIA_ASSET_TYPE_SHORT_VIDEO = "short_video") — the ONE value video
// briefs ever set payload.media.asset_type to; every image asset_type
// (ad_creative_image, social_visual, ...) falls through to "image".
const VIDEO_ASSET_TYPE = 'short_video';

function statusMessage(
  status: string | undefined,
  assetType: string | undefined
): string {
  if (!status) return 'Media generation status is unknown.';
  const byAssetType = STATUS_MESSAGES_BY_ASSET_TYPE[status];
  if (byAssetType) {
    return assetType === VIDEO_ASSET_TYPE ? byAssetType.video : byAssetType.image;
  }
  return (
    STATUS_MESSAGES_ASSET_TYPE_AGNOSTIC[status] ?? `Media is absent (${status}).`
  );
}

// T18/D3: video_pending is a benign, in-progress state, not a skipped/
// failed one — it reads as "come back soon", not "something went
// wrong", so it gets Polaris's neutral 'info' tone rather than the
// 'warning' every skipped_* status below still uses.
function statusTone(status: string | undefined): 'info' | 'warning' {
  return status === 'video_pending' ? 'info' : 'warning';
}

// How long before an asset's own expiry to proactively refresh — well
// short of media_storage_service.PRESIGNED_URL_EXPIRY_SECONDS (15
// minutes) so the refresh always has time to land before the URL
// actually goes stale.
const PROACTIVE_REFRESH_MARGIN_SECONDS = 60;
const MIN_REFRESH_DELAY_SECONDS = 5;

export function ArtifactMedia({ artifact }: { artifact: Artifact }) {
  const media = (artifact.payload as { media?: ArtifactMediaInfo } | undefined)
    ?.media;
  const [assets, setAssets] = useState<ArtifactAsset[]>([]);
  // `loading`: the FIRST load for this artifact — no assets on screen
  // yet, so a skeleton is the right placeholder.
  const [loading, setLoading] = useState(false);
  // `refreshing`: a BACKGROUND refresh (proactive timer or reactive
  // onError) while assets already exist — deliberately does not touch
  // `loading`, so the assets block never unmounts/shows a skeleton for
  // it. Distinct state (not reused) so the two are never conflated.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState(Boolean(media?.approved));
  const [approving, setApproving] = useState(false);

  // Request generation: switching between artifacts (or a background
  // refresh racing a foreground one) must not let a SLOWER, now-stale
  // fetch overwrite what a NEWER one already rendered (same discipline
  // MarketingAgentDetail already applies to its own loads/selections).
  const seq = useRef(0);
  // Guards the onError-triggered reactive refetch to exactly once per
  // load attempt — re-armed only by the image/video ACTUALLY loading
  // (see module docstring). Without this guard, a URL broken for a
  // reason OTHER than expiry (ad blocker, CSP, deleted object) would
  // refetch forever.
  const refetchedOnErrorRef = useRef(false);

  const fetchAssets = useCallback(
    (options?: { background?: boolean }) => {
      const current = ++seq.current;
      if (options?.background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      getArtifactAssets(artifact.id)
        .then((result) => {
          if (current !== seq.current) return;
          setAssets(result);
        })
        .catch((e) => {
          if (current !== seq.current) return;
          setError(
            e instanceof Error ? e.message : 'Failed to load media assets'
          );
        })
        .finally(() => {
          if (current !== seq.current) return;
          setLoading(false);
          setRefreshing(false);
        });
    },
    [artifact.id]
  );

  useEffect(() => {
    setApproved(Boolean(media?.approved));
    // A fresh artifact/status gets a fresh error-guard slate — this ref
    // otherwise carries over from whatever the PREVIOUS artifact left it
    // at, which could wrongly suppress this artifact's very first retry.
    refetchedOnErrorRef.current = false;
    if (!media || media.status !== 'generated') {
      setAssets([]);
      setError(null);
      setLoading(false);
      return;
    }
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact.id, media?.status]);

  // Proactive refresh, scheduled off whatever the CURRENT batch of
  // assets reports as its soonest expiry — re-runs whenever a fetch
  // lands a new `assets` array (including the refresh's own result, so
  // this reschedules itself). Cleared on unmount/re-render so a stale
  // timer from a previous batch never fires after a newer fetch already
  // superseded it. Always a BACKGROUND fetch: assets already exist by
  // construction here (the effect bails out otherwise), so this must
  // never flip `loading` and unmount the assets block out from under
  // whatever is still rendering fine.
  useEffect(() => {
    if (!assets.length) return;
    const soonestExpirySeconds = Math.min(...assets.map((a) => a.expires_in));
    const delaySeconds = Math.max(
      soonestExpirySeconds - PROACTIVE_REFRESH_MARGIN_SECONDS,
      MIN_REFRESH_DELAY_SECONDS
    );
    const timer = setTimeout(
      () => fetchAssets({ background: true }),
      delaySeconds * 1000
    );
    return () => clearTimeout(timer);
  }, [assets, fetchAssets]);

  const handleAssetLoadError = () => {
    if (refetchedOnErrorRef.current) return;
    refetchedOnErrorRef.current = true;
    fetchAssets({ background: true });
  };

  // The re-arm point (module docstring): only a REAL successful load —
  // not a merely-successful metadata fetch — proves the URL is
  // reachable, so this is the only place the guard resets.
  const handleAssetLoadSuccess = () => {
    refetchedOnErrorRef.current = false;
  };

  if (!media) return null;

  const approve = async () => {
    setApproving(true);
    try {
      const updated = await approveArtifact(artifact.id);
      const updatedMedia = (
        updated.payload as { media?: ArtifactMediaInfo } | undefined
      )?.media;
      setApproved(Boolean(updatedMedia?.approved));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingMd">
            Media
          </Text>
          {approved && <Badge tone="success">Approved</Badge>}
        </InlineStack>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {media.status !== 'generated' && (
          <Banner tone={statusTone(media.status)}>
            {statusMessage(media.status, media.asset_type)}
          </Banner>
        )}

        {media.status === 'generated' && loading && (
          <SkeletonBodyText lines={3} />
        )}

        {media.status === 'generated' &&
          !loading &&
          !refreshing &&
          assets.length === 0 && (
            <Banner tone="warning">
              No media file could be found for this artifact — it may have
              failed to upload, or the referenced asset no longer exists.
            </Banner>
          )}

        {media.status === 'generated' && assets.length > 0 && (
          <BlockStack gap="300">
            {assets.map((asset) => (
              <BlockStack gap="200" key={asset.id}>
                {asset.kind === 'image' && (
                  <img
                    src={asset.url}
                    alt="Generated marketing visual"
                    onError={handleAssetLoadError}
                    onLoad={handleAssetLoadSuccess}
                    style={{
                      maxWidth: '100%',
                      borderRadius: 8,
                      display: 'block',
                    }}
                  />
                )}
                {asset.kind === 'video' && (
                  <video
                    src={asset.url}
                    controls
                    onError={handleAssetLoadError}
                    onLoadedData={handleAssetLoadSuccess}
                    style={{ maxWidth: '100%', borderRadius: 8 }}
                  />
                )}
                {asset.kind !== 'image' && asset.kind !== 'video' && (
                  <Text as="p" tone="subdued">
                    {`Unsupported asset kind: ${asset.kind}`}
                  </Text>
                )}
                <InlineStack gap="200">
                  {/* "Open full size", not "Download": the presigned URL
                      is cross-origin, and browsers ignore the `download`
                      attribute on cross-origin links — this always just
                      OPENS the asset in a new tab, so the label says
                      that rather than promising a save-to-disk this
                      button cannot actually deliver. */}
                  <Button
                    url={asset.url}
                    target="_blank"
                    variant="tertiary"
                    size="slim"
                  >
                    Open full size
                  </Button>
                </InlineStack>
              </BlockStack>
            ))}
          </BlockStack>
        )}

        {/* Gated to a real generated outcome — approving a skipped_*
            (zero-asset) status has nothing for a human to actually
            review. Known limitation: approve is ONE-WAY (no "unapprove"
            control) — clearing a mistaken approval currently requires a
            direct data fix, not a UI action. */}
        {media.status === 'generated' && (
          <InlineStack gap="200">
            <Button
              onClick={() => void approve()}
              loading={approving}
              disabled={approved}
              size="slim"
            >
              {approved ? 'Approved' : 'Approve'}
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Card>
  );
}
